/**
 * Settings — account + sign out.
 *
 * This screen holds the ONLY logout control in the app. It previously wasn't
 * routed (App.jsx), so a signed-in client had no way to log out through the
 * UI; it's now reachable from the TopBar account button and /settings.
 *
 * Restyled from the earlier Tailwind-utility scaffolding to the shipped
 * "Site Office" system (panel / lrow / mono / d-xl). The old Integrations row
 * linked to an unrouted, not-configured page and was dropped.
 *
 * Data: GET /api/console/me — { client: { business_name, owner_name, email, tier } }
 */
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LogOut } from 'lucide-react';
import { session, apiFetch } from '../lib/api.js';

const TIER_LABEL = {
  foundation: 'Foundation',
  lead_recovery: 'Lead Recovery',
  always_open: 'Always Open',
};

export default function Settings() {
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ['me'],
    queryFn: () => apiFetch('/api/console/me'),
  });
  const client = data?.client || {};

  function logout() {
    session.clear();
    navigate('/login', { replace: true });
  }

  const rows = [
    { label: 'Business', value: client.business_name },
    { label: 'Account', value: client.owner_name },
    { label: 'Email', value: client.email },
    { label: 'Plan', value: TIER_LABEL[client.tier] || client.tier },
  ].filter((r) => r.value);

  return (
    <div style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <h1 className="d-xl" style={{ fontSize: 30, color: 'var(--ink)', margin: '0 0 4px' }}>
          Settings
        </h1>
        <p className="serif-i" style={{ margin: 0, color: 'var(--ink-soft)', fontSize: 16, lineHeight: 1.55 }}>
          Your account and this device.
        </p>
      </div>

      {rows.length > 0 && (
        <section aria-label="Account" className="panel" style={{ padding: 20 }}>
          <i className="brk brk-tl" /><i className="brk brk-br" />
          <h2 className="mlabel" style={{ margin: '0 0 14px' }}>Account</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {rows.map((r) => (
              <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'baseline' }}>
                <span className="mono" style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-soft)', flexShrink: 0 }}>
                  {r.label}
                </span>
                <span style={{ fontSize: 14, color: 'var(--ink)', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {r.value}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      <button
        type="button"
        onClick={logout}
        className="btnx quiet"
        style={{ alignSelf: 'flex-start', color: 'var(--bad)', borderColor: 'var(--bad)' }}
      >
        <LogOut size={15} strokeWidth={2} aria-hidden="true" />
        <span>Log out</span>
      </button>
    </div>
  );
}
